import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService, attendanceService } from '../services/api';
import Navbar from '../components/Navbar';
import './Profile.css';

const Profile = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    labId: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [stats, setStats] = useState({
    totalDays: 0,
    presentDays: 0,
    percentage: 0,
    currentStreak: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await authService.getMe();
        setFormData(prev => ({
          ...prev,
          name: userData.name || '',
          email: userData.email || '',
          labId: userData.labId || ''
        }));

        // Fetch attendance stats
        const currentDate = new Date();
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        const attendance = await attendanceService.getMyAttendance(month, year);

        if (attendance && attendance.length > 0) {
          const presentDays = attendance.filter(a => a.status === 'present').length;
          // Calculate working days up to today (excluding weekends)
          const today = currentDate.getDate();
          let workingDays = 0;
          for (let day = 1; day <= today; day++) {
            const date = new Date(year, month - 1, day);
            const dayOfWeek = date.getDay();
            // Skip Saturday (6) and Sunday (0)
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
              workingDays++;
            }
          }
          const percentage = workingDays > 0 ? Math.round((presentDays / workingDays) * 100) : 0;

          // Calculate current streak
          let streak = 0;
          const sortedAttendance = [...attendance].sort((a, b) =>
            new Date(b.date) - new Date(a.date)
          );
          for (const record of sortedAttendance) {
            if (record.status === 'present') {
              streak++;
            } else {
              break;
            }
          }

          setStats({
            totalDays: workingDays,
            presentDays,
            percentage,
            currentStreak: streak
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ type: '', text: '' });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await authService.updateProfile({
        name: formData.name,
        labId: formData.labId
      });

      // Update local storage
      const currentUser = authService.getCurrentUser();
      const updatedUser = { ...currentUser, name: formData.name, labId: formData.labId };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update profile'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmNewPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (formData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await authService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      }));

      setMessage({ type: 'success', text: 'Password changed successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to change password'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <Navbar />
        <div className="profile-container">
          <div className="loading-state">
            <div className="loading-spinner-large"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Navbar />
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-avatar">
            {formData.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="profile-header-info">
            <h1>{formData.name}</h1>
            <p className="profile-role">{user?.role === 'admin' ? 'Administrator' : 'Lab Member'}</p>
            <p className="profile-email">{formData.email}</p>
          </div>
        </div>

        <div className="profile-stats">
          <div className="stat-box">
            <span className="stat-icon">📊</span>
            <span className="stat-value">{stats.presentDays}</span>
            <span className="stat-label">Days Present</span>
          </div>
          <div className="stat-box">
            <span className="stat-icon">📅</span>
            <span className="stat-value">{stats.totalDays}</span>
            <span className="stat-label">Working Days</span>
          </div>
          <div className="stat-box">
            <span className="stat-icon">📈</span>
            <span className="stat-value">{stats.percentage}%</span>
            <span className="stat-label">Attendance Rate</span>
          </div>
          <div className="stat-box">
            <span className="stat-icon">🔥</span>
            <span className="stat-value">{stats.currentStreak}</span>
            <span className="stat-label">Current Streak</span>
          </div>
        </div>

        <div className="profile-tabs">
          <button
            className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            Personal Information
          </button>
          <button
            className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {activeTab === 'info' && (
          <div className="profile-card">
            <h2>Personal Information</h2>
            <form onSubmit={handleUpdateProfile}>
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  disabled
                  className="disabled"
                />
                <small>Email cannot be changed</small>
              </div>
              <div className="form-group">
                <label htmlFor="labId">Lab ID / Roll Number</label>
                <input
                  type="text"
                  id="labId"
                  name="labId"
                  value={formData.labId}
                  onChange={handleChange}
                  placeholder="e.g., RNA-2024-001"
                />
              </div>
              <button type="submit" className="save-btn" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="profile-card">
            <h2>Change Password</h2>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmNewPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmNewPassword"
                  name="confirmNewPassword"
                  value={formData.confirmNewPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                />
              </div>
              <button type="submit" className="save-btn" disabled={saving}>
                {saving ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
